import { useState } from "react";

type Channel = "kakao" | "line";
type InquiryStatus = "unread" | "read" | "replied";

interface Inquiry {
  id: number;
  name: string;
  channel: Channel;
  channelId: string;
  date: string;
  time: string;
  message: string;
  accommodation?: string;
  status: InquiryStatus;
}

const mockInquiries: Inquiry[] = [
  {
    id: 1,
    name: "김지수",
    channel: "kakao",
    channelId: "jisoo_kim92",
    date: "2025.04.14",
    time: "오후 3:22",
    message: "안녕하세요! 텐진 스튜디오 4월 20일부터 22일까지 예약 가능한지 확인 부탁드려요. 2명이고 조식 포함되나요?",
    accommodation: "텐진 감성 한옥 스튜디오",
    status: "unread",
  },
  {
    id: 2,
    name: "박서준",
    channel: "line",
    channelId: "seojun.park",
    date: "2025.04.14",
    time: "오후 1:10",
    message: "나카스 리버뷰 룸 문의드립니다. 저희가 5월 초에 커플로 가는데 야경 뷰가 실제로도 사진처럼 좋은가요? 후기 보고 너무 가고 싶어서요!",
    accommodation: "나카스 리버뷰 로맨틱 룸",
    status: "unread",
  },
  {
    id: 3,
    name: "이수민",
    channel: "kakao",
    channelId: "sumin_lee",
    date: "2025.04.13",
    time: "오전 11:45",
    message: "하카타 패밀리 아파트 6인 가능한지요? 부모님, 아이들 포함해서 6명인데 침대 구성이 어떻게 되나요?",
    accommodation: "하카타 럭셔리 패밀리 아파트",
    status: "read",
  },
  {
    id: 4,
    name: "최유리",
    channel: "line",
    channelId: "yuri.choi2",
    date: "2025.04.13",
    time: "오전 9:30",
    message: "오호리 공원 하우스 다음 달 주말 예약 가능한지 보고 싶어요. 에어비앤비 링크로 바로 예약하면 되나요?",
    accommodation: "오호리 공원 뷰 모던 하우스",
    status: "replied",
  },
  {
    id: 5,
    name: "정민호",
    channel: "kakao",
    channelId: "minho_jjj",
    date: "2025.04.12",
    time: "오후 7:55",
    message: "야쿠인 빈티지 룸 자전거 무료 대여가 진짜인가요? 야쿠인에서 오호리 공원까지 얼마나 걸리나요? 혼자 여행 예정이에요.",
    accommodation: "야쿠인 카페 거리 빈티지 룸",
    status: "replied",
  },
  {
    id: 6,
    name: "강하나",
    channel: "line",
    channelId: "hana.kang99",
    date: "2025.04.11",
    time: "오후 4:11",
    message: "신혼여행으로 후쿠오카 고려 중인데, 마이즈루 아파트 꽃 장식 서비스 따로 신청해야 하나요? 비용은 얼마인지도요!",
    accommodation: "마이즈루 파크 옆 신혼 아파트",
    status: "replied",
  },
];

const channelStyle: Record<Channel, { bg: string; text: string; icon: string; label: string }> = {
  kakao: { bg: "bg-yellow-100", text: "text-yellow-800", icon: "ri-kakao-talk-fill", label: "카카오톡" },
  line: { bg: "bg-emerald-100", text: "text-emerald-700", icon: "ri-line-fill", label: "라인" },
};

const statusStyle: Record<InquiryStatus, { label: string; className: string }> = {
  unread: { label: "미확인", className: "bg-red-100 text-red-600" },
  read: { label: "확인 완료", className: "bg-stone-100 text-stone-500" },
  replied: { label: "답변 완료", className: "bg-emerald-100 text-emerald-700" },
};

export default function InquiriesTab() {
  const [inquiries, setInquiries] = useState<Inquiry[]>(mockInquiries);
  const [filter, setFilter] = useState<"all" | Channel | InquiryStatus>("all");
  const [expanded, setExpanded] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const markStatus = (id: number, status: InquiryStatus) => {
    setInquiries((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
    showToast(status === "replied" ? "답변 완료로 변경됐어요." : "확인 완료로 표시됐어요.");
  };

  const filtered =
    filter === "all"
      ? inquiries
      : inquiries.filter((q) => q.channel === filter || q.status === filter);

  const unreadCount = inquiries.filter((q) => q.status === "unread").length;

  return (
    <div className="max-w-4xl">
      {toast && (
        <div className="fixed top-6 right-6 z-50 bg-stone-800 text-white text-sm px-4 py-3 rounded-lg">
          {toast}
        </div>
      )}

      <div className="mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-stone-800">문의 확인</h2>
          {unreadCount > 0 && (
            <span className="px-2.5 py-0.5 bg-red-500 text-white text-xs rounded-full font-medium">
              미확인 {unreadCount}건
            </span>
          )}
        </div>
        <p className="text-sm text-stone-400 mt-1">카카오톡 · 라인으로 들어온 문의를 확인하세요.</p>
      </div>

      {/* 필터 */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {(["all", "kakao", "line", "unread", "read", "replied"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-all ${
              filter === f ? "bg-stone-800 text-white" : "bg-white text-stone-500 border border-stone-200 hover:bg-stone-50"
            }`}
          >
            {f === "all" ? "전체" : f === "kakao" ? "카카오톡" : f === "line" ? "라인" : f === "unread" ? "미확인" : f === "read" ? "확인 완료" : "답변 완료"}
          </button>
        ))}
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: "전체 문의", value: inquiries.length, icon: "ri-message-3-line", color: "text-stone-600" },
          { label: "카카오톡", value: inquiries.filter((q) => q.channel === "kakao").length, icon: "ri-chat-1-line", color: "text-yellow-600" },
          { label: "라인", value: inquiries.filter((q) => q.channel === "line").length, icon: "ri-chat-2-line", color: "text-emerald-600" },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl border border-stone-100 p-4 flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-stone-50">
              <i className={`${stat.icon} ${stat.color} text-base`}></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800">{stat.value}</p>
              <p className="text-xs text-stone-400">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* 문의 목록 */}
      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="text-center py-16 text-stone-400 bg-white rounded-xl border border-stone-100">
            해당 조건의 문의가 없어요.
          </div>
        )}
        {filtered.map((inquiry) => {
          const ch = channelStyle[inquiry.channel];
          const st = statusStyle[inquiry.status];
          const isExpanded = expanded === inquiry.id;

          return (
            <div
              key={inquiry.id}
              className={`bg-white rounded-xl border transition-all ${
                inquiry.status === "unread" ? "border-stone-300" : "border-stone-100"
              }`}
            >
              <div
                className="flex items-center gap-4 p-4 cursor-pointer"
                onClick={() => {
                  setExpanded(isExpanded ? null : inquiry.id);
                  if (inquiry.status === "unread") markStatus(inquiry.id, "read");
                }}
              >
                {inquiry.status === "unread" && (
                  <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"></span>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ch.bg} ${ch.text}`}>
                      <i className={`${ch.icon} mr-0.5`}></i>{ch.label}
                    </span>
                    <span className="font-medium text-stone-800 text-sm">{inquiry.name}</span>
                    <span className="text-xs text-stone-400">{inquiry.channelId}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.className}`}>
                      {st.label}
                    </span>
                  </div>
                  <p className="text-sm text-stone-500 mt-1 truncate">{inquiry.message}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-stone-400">{inquiry.date}</p>
                  <p className="text-xs text-stone-400">{inquiry.time}</p>
                </div>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${isExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-stone-400`}></i>
                </div>
              </div>

              {isExpanded && (
                <div className="px-4 pb-4 border-t border-stone-50">
                  <p className="text-sm text-stone-600 leading-relaxed pt-3">{inquiry.message}</p>
                  {inquiry.accommodation && (
                    <p className="text-xs text-stone-400 mt-2">
                      <i className="ri-home-line mr-1"></i>관심 숙소: {inquiry.accommodation}
                    </p>
                  )}
                  <div className="flex gap-2 mt-3">
                    <a
                      href={inquiry.channel === "kakao" ? "https://pf.kakao.com" : "https://line.me"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap ${ch.bg} ${ch.text} hover:opacity-80 transition-opacity`}
                    >
                      <i className={ch.icon}></i>
                      {ch.label}로 답장하기
                    </a>
                    {inquiry.status !== "replied" && (
                      <button
                        onClick={() => markStatus(inquiry.id, "replied")}
                        className="px-4 py-2 bg-stone-800 text-white text-xs rounded-lg cursor-pointer whitespace-nowrap hover:bg-stone-700"
                      >
                        답변 완료 처리
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
